/**
 * Job Detail Page
 * Displays detailed job information and application form
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  MapPin,
  Briefcase,
  Clock,
  DollarSign,
  ChevronRight,
  Star,
  Loader2,
  Building2,
  Calendar,
  Eye,
  Users,
  Zap,
  Home,
  Laptop,
  CheckCircle2,
  Mail,
  Phone,
  ExternalLink,
  Share2,
  BookmarkPlus,
  ArrowLeft,
  GraduationCap,
  Award,
  Send,
} from 'lucide-react';
import { useCountry } from '../context/CountryContext';
import { useToast } from '../hooks/use-toast';
import { ImproveYourChancesSection, PostApplicationPrompt } from '../components/services/ITServicesSection';
import { JobServiceTriggerPrompt } from '../components/services/JobCareerServicesSection';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Work style icons
const WORK_STYLE_ICONS = {
  'on-site': Building2,
  'remote': Laptop,
  'hybrid': Home,
};

const EXPERIENCE_LABELS = {
  'entry': 'Entry Level',
  'junior': 'Junior (1-2 years)',
  'mid': 'Mid Level (3-5 years)',
  'senior': 'Senior (5+ years)',
  'executive': 'Executive',
};

const JobDetailPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { country } = useCountry();
  const { toast } = useToast();
  
  const [job, setJob] = useState(null);
  const [similarJobs, setSimilarJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState({ symbol: 'Rs', code: 'LKR' });
  const [showApplyDialog, setShowApplyDialog] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applicationSubmitted, setApplicationSubmitted] = useState(false);
  
  // Application form state
  const [applicationForm, setApplicationForm] = useState({
    applicant_name: '',
    applicant_email: '',
    applicant_phone: '',
    cover_letter: '',
    expected_salary: '',
    available_from: '',
  });

  // Fetch job details
  useEffect(() => {
    const fetchJob = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/api/jobs/listings/${jobId}`, {
          params: { country }
        });
        
        if (response.data.job) {
          setJob(response.data.job);
          setSimilarJobs(response.data.similar_jobs || []);
          setCurrency(response.data.currency);
        }
      } catch (error) {
        console.error('Error fetching job:', error);
        toast({
          title: 'Error',
          description: 'Failed to load job details',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };
    
    if (jobId) {
      fetchJob();
    }
  }, [jobId, country, toast]);

  // Format salary display
  const formatSalary = () => {
    if (!job) return '';
    if (!job.salary_min && !job.salary_max) return 'Negotiable';
    if (job.salary_min && job.salary_max) {
      if (job.salary_min === job.salary_max) {
        return `${currency.symbol} ${job.salary_min.toLocaleString()}`;
      }
      return `${currency.symbol} ${job.salary_min.toLocaleString()} - ${job.salary_max.toLocaleString()}`;
    }
    if (job.salary_min) return `From ${currency.symbol} ${job.salary_min.toLocaleString()}`;
    return `Up to ${currency.symbol} ${job.salary_max.toLocaleString()}`;
  };

  const getPayPeriod = () => {
    if (!job) return '';
    const periods = {
      'hourly': 'per hour',
      'daily': 'per day',
      'weekly': 'per week',
      'monthly': 'per month',
      'project': 'per project',
    };
    return periods[job.pay_type] || 'per month';
  };

  // Handle application submission
  const handleApply = async (e) => {
    e.preventDefault();
    
    if (!applicationForm.applicant_name || !applicationForm.applicant_email || !applicationForm.applicant_phone) {
      toast({
        title: 'Missing Information',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }
    
    try {
      setApplying(true);
      const response = await axios.post(`${API_URL}/api/jobs/apply`, {
        job_id: jobId,
        ...applicationForm,
        expected_salary: applicationForm.expected_salary ? parseFloat(applicationForm.expected_salary) : null,
      });
      
      if (response.data.success) {
        toast({
          title: 'Application Submitted!',
          description: `Reference: ${response.data.application_id}`,
        });
        setShowApplyDialog(false);
        setApplicationSubmitted(true);
        setApplicationForm({
          applicant_name: '',
          applicant_email: '',
          applicant_phone: '',
          cover_letter: '',
          expected_salary: '',
          available_from: '',
        });
      }
    } catch (error) {
      toast({
        title: 'Application Failed',
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setApplying(false);
    }
  };

  // Share job
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: job.title,
          text: `${job.title} at ${job.company_name}`,
          url: window.location.href,
        });
      } catch (error) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast({ title: 'Link copied to clipboard' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading job details...</span>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <Briefcase className="h-16 w-16 text-gray-300 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Job Not Found</h2>
        <p className="text-gray-500 mb-4">This job may have been removed or expired</p>
        <Button onClick={() => navigate('/jobs')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Browse Jobs
        </Button>
      </div>
    );
  }

  const WorkStyleIcon = WORK_STYLE_ICONS[job.work_style] || Building2;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="job-detail-page">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
            <Link to="/" className="hover:text-blue-600">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <Link to="/jobs" className="hover:text-blue-600">Jobs</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-gray-900 truncate">{job.title}</span>
          </div>
          
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
            <div className="flex-1">
              {/* Badges */}
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline" className="text-xs">
                  {job.category_icon} {job.category_name}
                </Badge>
                {job.featured && (
                  <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">
                    <Star className="h-3 w-3 mr-1" />
                    Featured
                  </Badge>
                )}
                {job.urgent && (
                  <Badge className="bg-red-100 text-red-700 border-red-200 text-xs">
                    <Zap className="h-3 w-3 mr-1" />
                    Urgent Hiring
                  </Badge>
                )}
              </div>
              
              {/* Title & Company */}
              <h1 className="text-2xl font-bold text-gray-900 mb-1">{job.title}</h1>
              <p className="text-lg text-gray-600">{job.company_name}</p>
              
              {/* Meta Info */}
              <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {job.location}
                </span>
                <span className="flex items-center gap-1">
                  <WorkStyleIcon className="h-4 w-4" />
                  {job.work_style === 'on-site' ? 'On-site' : job.work_style === 'remote' ? 'Remote' : 'Hybrid'}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  {job.employment_type === 'full-time' ? 'Full-time' : 
                   job.employment_type === 'part-time' ? 'Part-time' : 
                   job.employment_type}
                </span>
                <span className="flex items-center gap-1">
                  <Eye className="h-4 w-4" />
                  {job.views} views
                </span>
                <span className="flex items-center gap-1">
                  <Users className="h-4 w-4" />
                  {job.applications_count} applications
                </span>
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex flex-col items-end gap-2">
              <div className="text-right">
                <div className="text-2xl font-bold text-green-600">{formatSalary()}</div>
                <div className="text-sm text-gray-500">{getPayPeriod()}</div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleShare}>
                  <Share2 className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm">
                  <BookmarkPlus className="h-4 w-4" />
                </Button>
                <Button 
                  size="lg" 
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => setShowApplyDialog(true)}
                  data-testid="apply-now-button"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Apply Now
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Job Description */}
            <Card>
              <CardHeader>
                <CardTitle>Job Description</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 whitespace-pre-line">{job.description}</p>
              </CardContent>
            </Card>

            {/* Responsibilities */}
            {job.responsibilities && job.responsibilities.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Responsibilities</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {job.responsibilities.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Requirements */}
            {job.requirements && job.requirements.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Requirements</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {job.requirements.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle2 className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Benefits */}
            {job.benefits && job.benefits.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Benefits</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {job.benefits.map((item, idx) => (
                      <Badge key={idx} variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        {item}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Job Career Services Trigger - Shows for supported categories */}
            {!applicationSubmitted && (
              <JobServiceTriggerPrompt 
                jobCategory={job.category} 
                triggerType="view_jobs" 
              />
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Job Overview */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Job Overview</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <Briefcase className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Employment Type</p>
                    <p className="font-medium capitalize">{job.employment_type?.replace('-', ' ')}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-50 rounded-lg">
                    <WorkStyleIcon className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Work Style</p>
                    <p className="font-medium capitalize">{job.work_style?.replace('-', ' ')}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-50 rounded-lg">
                    <Award className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Experience Level</p>
                    <p className="font-medium">{EXPERIENCE_LABELS[job.experience_level] || job.experience_level}</p>
                  </div>
                </div>
                
                {job.education_required && (
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-50 rounded-lg">
                      <GraduationCap className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Education</p>
                      <p className="font-medium">{job.education_required}</p>
                    </div>
                  </div>
                )}
                
                {job.vacancies > 1 && (
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-cyan-50 rounded-lg">
                      <Users className="h-5 w-5 text-cyan-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Vacancies</p>
                      <p className="font-medium">{job.vacancies} positions</p>
                    </div>
                  </div>
                )}
                
                {job.schedule && (
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-50 rounded-lg">
                      <Clock className="h-5 w-5 text-indigo-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Schedule</p>
                      <p className="font-medium capitalize">{job.schedule?.replace('-', ' ')}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Skills */}
            {job.skills_required && job.skills_required.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Required Skills</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {job.skills_required.map((skill, idx) => (
                      <Badge key={idx} variant="outline">{skill}</Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Contact */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Contact</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {job.contact_email && (
                  <a 
                    href={`mailto:${job.contact_email}`}
                    className="flex items-center gap-2 text-blue-600 hover:underline"
                  >
                    <Mail className="h-4 w-4" />
                    {job.contact_email}
                  </a>
                )}
                {job.contact_phone && (
                  <a 
                    href={`tel:${job.contact_phone}`}
                    className="flex items-center gap-2 text-blue-600 hover:underline"
                  >
                    <Phone className="h-4 w-4" />
                    {job.contact_phone}
                  </a>
                )}
                {job.apply_url && (
                  <a 
                    href={job.apply_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-blue-600 hover:underline"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Apply on company website
                  </a>
                )}
              </CardContent>
            </Card>

            {/* Apply CTA */}
            <Button 
              className="w-full bg-blue-600 hover:bg-blue-700" 
              size="lg"
              onClick={() => setShowApplyDialog(true)}
            >
              <Send className="h-4 w-4 mr-2" />
              Apply Now
            </Button>
          </div>
        </div>

        {/* Similar Jobs */}
        {similarJobs.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-semibold mb-4">Similar Jobs</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {similarJobs.map((similarJob) => (
                <Card 
                  key={similarJob.id}
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate(`/jobs/${similarJob.id}`)}
                >
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-1">{similarJob.title}</h3>
                    <p className="text-sm text-gray-500 mb-2">{similarJob.company_name}</p>
                    <div className="flex items-center gap-3 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3.5 w-3.5" />
                        {similarJob.location}
                      </span>
                      <span className="text-green-600 font-medium">
                        {similarJob.salary_min ? `${currency.symbol} ${similarJob.salary_min.toLocaleString()}` : 'Negotiable'}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Apply Dialog */}
      <Dialog open={showApplyDialog} onOpenChange={setShowApplyDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Apply for {job.title}</DialogTitle>
            <DialogDescription>
              at {job.company_name}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleApply} className="space-y-4">
            <div>
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                value={applicationForm.applicant_name}
                onChange={(e) => setApplicationForm(prev => ({ ...prev, applicant_name: e.target.value }))}
                placeholder="Enter your full name"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={applicationForm.applicant_email}
                onChange={(e) => setApplicationForm(prev => ({ ...prev, applicant_email: e.target.value }))}
                placeholder="your@email.com"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                value={applicationForm.applicant_phone}
                onChange={(e) => setApplicationForm(prev => ({ ...prev, applicant_phone: e.target.value }))}
                placeholder={country === "UK" ? "+44 7XXX XXXXXX" : "+94 77 XXX XXXX"}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="salary">Expected Salary ({currency.symbol})</Label>
              <Input
                id="salary"
                type="number"
                value={applicationForm.expected_salary}
                onChange={(e) => setApplicationForm(prev => ({ ...prev, expected_salary: e.target.value }))}
                placeholder="e.g., 100000"
              />
            </div>
            
            <div>
              <Label htmlFor="available">Available From</Label>
              <Input
                id="available"
                type="date"
                value={applicationForm.available_from}
                onChange={(e) => setApplicationForm(prev => ({ ...prev, available_from: e.target.value }))}
              />
            </div>
            
            <div>
              <Label htmlFor="cover">Cover Letter</Label>
              <Textarea
                id="cover"
                value={applicationForm.cover_letter}
                onChange={(e) => setApplicationForm(prev => ({ ...prev, cover_letter: e.target.value }))}
                placeholder="Tell us why you're a good fit for this role..."
                rows={4}
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowApplyDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={applying} className="bg-blue-600 hover:bg-blue-700">
                {applying ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Submit Application
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobDetailPage;
